import * as Dialog from "@radix-ui/react-dialog";
import {
    DraftingCompass,
    Globe,
    type LucideIcon,
    Monitor,
    Moon,
    Plus,
    Search,
    Sun,
    Users,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useTheme } from "next-themes";
import { Resource } from "@exibra/shared";
import { Routes } from "@/router/routes";
import { cn } from "@/lib/utils";

interface CommandPaletteProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface CommandItem {
    group: string;
    label: string;
    icon: LucideIcon;
    hint?: string;
    run: () => void;
}

/**
 * ⌘K command palette: fuzzy-filterable navigation and quick actions, wired to real routes.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id: tenantId } = useParams();
    const { resolvedTheme, setTheme } = useTheme();
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    const base = `/${Routes.Tenant}/${tenantId}`;

    const items = useMemo<CommandItem[]>(() => {
        const go = (path: string) => () => {
            onOpenChange(false);
            navigate(path);
        };
        return [
            {
                group: t("command.groups.goTo"),
                label: t("screens.title"),
                icon: Monitor,
                hint: "G S",
                run: go(`${base}/${Resource.Screen}`),
            },
            {
                group: t("command.groups.goTo"),
                label: t("templates.title"),
                icon: DraftingCompass,
                hint: "G T",
                run: go(`${base}/${Resource.Template}`),
            },
            {
                group: t("command.groups.goTo"),
                label: t("applications.title"),
                icon: Globe,
                hint: "G A",
                run: go(`${base}/${Resource.WebsiteApplication}`),
            },
            {
                group: t("command.groups.goTo"),
                label: t("users.title"),
                icon: Users,
                hint: "G M",
                run: go(`${base}/${Resource.User}`),
            },
            {
                group: t("command.groups.actions"),
                label: t("command.actions.pairScreen"),
                icon: Plus,
                run: go(`${base}/${Resource.Screen}`),
            },
            {
                group: t("command.groups.actions"),
                label:
                    resolvedTheme === "dark"
                        ? t("command.actions.lightMode")
                        : t("command.actions.darkMode"),
                icon: resolvedTheme === "dark" ? Sun : Moon,
                run: () => {
                    setTheme(resolvedTheme === "dark" ? "light" : "dark");
                    onOpenChange(false);
                },
            },
        ];
    }, [t, base, navigate, onOpenChange, resolvedTheme, setTheme]);

    const filtered = useMemo(
        () => items.filter((it) => it.label.toLowerCase().includes(query.toLowerCase())),
        [items, query],
    );

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            filtered[active]?.run();
        }
    };

    let lastGroup: string | null = null;

    return (
        <Dialog.Root
            open={open}
            onOpenChange={(o) => {
                onOpenChange(o);
                if (o) {
                    setQuery("");
                    setActive(0);
                }
            }}
        >
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[60] bg-brand-ink/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
                <Dialog.Content
                    onKeyDown={handleKeyDown}
                    className="fixed left-1/2 top-[14vh] z-[60] w-[540px] max-w-[calc(100vw-2rem)] -translate-x-1/2 overflow-hidden rounded-xl border bg-card shadow-xl data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
                >
                    <Dialog.Title className="sr-only">{t("command.title")}</Dialog.Title>
                    <Dialog.Description className="sr-only">
                        {t("command.placeholder")}
                    </Dialog.Description>
                    <div className="flex items-center gap-2.5 border-b px-3.5 py-3">
                        <Search className="size-3.5 text-muted-foreground" />
                        <input
                            autoFocus
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setActive(0);
                            }}
                            placeholder={t("command.placeholder")}
                            className="flex-1 border-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                        />
                        <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10.5px] text-muted-foreground">
                            esc
                        </kbd>
                    </div>
                    <div ref={listRef} className="max-h-[340px] overflow-auto p-1.5">
                        {filtered.length === 0 ? (
                            <p className="px-2.5 py-6 text-center text-xs text-muted-foreground">
                                {t("general.noResults")}
                            </p>
                        ) : (
                            filtered.map((item, i) => {
                                const showHeader = item.group !== lastGroup;
                                lastGroup = item.group;
                                const Icon = item.icon;
                                return (
                                    <div key={`${item.group}-${item.label}`}>
                                        {showHeader && (
                                            <div className="px-2.5 pb-1 pt-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                                {item.group}
                                            </div>
                                        )}
                                        <button
                                            onMouseEnter={() => setActive(i)}
                                            onClick={() => item.run()}
                                            className={cn(
                                                "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-[12.5px]",
                                                i === active && "bg-muted",
                                            )}
                                        >
                                            <Icon className="size-3.5 text-muted-foreground" />
                                            <span className="flex-1">{item.label}</span>
                                            {item.hint && (
                                                <span className="font-mono text-[10.5px] text-muted-foreground">
                                                    {item.hint}
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
